import { BarChart3, ArrowUpRight, ArrowDownRight, ChevronRight, Lightbulb } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { P, DIM_COLORS, DIM_ICONS, SectionTitle, DimBar } from "./report-utils";

export function DimensionsTab({ dimScores, total, strengths, devAreas }) {
    const radarData = Object.values(dimScores).map(d => ({ dim: d.short_name, score: d.normalized, fullMark: 100 }));

    return (
        <div data-testid="section-dimensions">
            <SectionTitle icon={BarChart3} title="Dimension Insights" subtitle="Behavioral profile across 6 coachability dimensions" id="dimensions" />

            <div className="grid lg:grid-cols-5 gap-6 mb-6">
                <div className="lg:col-span-3 rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
                    <p className="text-[0.65rem] font-bold uppercase tracking-[0.15em] text-slate-400 mb-4">Behavioral Radar</p>
                    <div className="h-[320px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
                                <PolarGrid stroke="#E2E8F0" strokeWidth={0.8} />
                                <PolarAngleAxis dataKey="dim" tick={{ fill: "#475569", fontSize: 11, fontWeight: 600 }} />
                                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 9 }} axisLine={false} />
                                <Radar dataKey="score" stroke={P.blue} fill={P.blue} fillOpacity={0.12} strokeWidth={2.5} dot={{ r: 4, fill: P.blue, stroke: "#fff", strokeWidth: 2 }} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="lg:col-span-2 space-y-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <ArrowUpRight className="h-4 w-4 text-emerald-600" />
                            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Strength Areas</p>
                        </div>
                        {strengths.map((d) => (
                            <div key={d.short_name} className="flex items-center gap-2 mb-2 last:mb-0">
                                <ChevronRight className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                                <span className="text-sm text-emerald-800 font-medium">{d.short_name}</span>
                                <span className="ml-auto text-sm font-bold text-emerald-700">{d.normalized.toFixed(0)}%</span>
                            </div>
                        ))}
                    </div>
                    <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <ArrowDownRight className="h-4 w-4 text-amber-600" />
                            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Development Edge</p>
                        </div>
                        {devAreas.map((d) => (
                            <div key={d.short_name} className="flex items-center gap-2 mb-2 last:mb-0">
                                <ChevronRight className="h-3 w-3 text-amber-500 flex-shrink-0" />
                                <span className="text-sm text-amber-800 font-medium">{d.short_name}</span>
                                <span className="ml-auto text-sm font-bold text-amber-700">{d.normalized.toFixed(0)}%</span>
                            </div>
                        ))}
                    </div>
                    <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <Lightbulb className="h-4 w-4 text-blue-600" />
                            <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">What This Means</p>
                        </div>
                        <p className="text-sm text-blue-800 leading-relaxed">
                            {total >= 70
                                ? "Strong foundation across dimensions. Focus coaching on converting awareness into sustained behavioral change."
                                : total >= 45
                                    ? "Mixed profile with clear growth levers. Target development areas while reinforcing existing strengths."
                                    : "Foundational work needed. Build psychological safety before pushing for behavioral shifts."
                            }
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-3">
                {Object.values(dimScores).map((d, i) => (
                    <DimBar key={d.short_name} dim={d} color={DIM_COLORS[i % DIM_COLORS.length]}
                        icon={DIM_ICONS[i % DIM_ICONS.length]} />
                ))}
            </div>
        </div>
    );
}
